'use client'

import { Validation } from '@vinted/web-ui'
import { useEffect, useState } from 'react'

import { useDataDomeCaptcha } from '@marketplace-web/domain/data-dome'
import { VerificationType, VerificationTrigger } from '@marketplace-web/domain/verification-data'
import { navigateToPage, useLocation } from '@marketplace-web/shared/browser'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useSession } from '@marketplace-web/shared/session'
import { Notification } from '@marketplace-web/common-components/notification-ui'
import { PhoneNumberDto, createPhoneNumber } from '@marketplace-web/domain/phone-number-data'

import { FaqEntryType } from 'constants/faq-entry'
import { ROOT_URL, SECURITY_SETTINGS_2FA_URL, SECURITY_SETTINGS_URL } from 'constants/routes'
import { HttpStatus } from '@marketplace-web/shared/api-client'
import { useRefUrl } from '@marketplace-web/shared/ref-url'

import PhoneNumberForm from '../../components/PhoneNumberForm'
import TwoFactorVerification from '../../components/TwoFactorVerification'

const CreatePhoneNumber = () => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)
  const [error, setError] = useState<string>()
  const [phoneNumberData, setPhoneNumberData] = useState<PhoneNumberDto>()
  const [isTooManyAttempts, setIsTooManyAttempts] = useState(false)
  const { searchParams } = useLocation()
  const isAccountProtectEnabled = useFeatureSwitch('ap_registration_flow')
  const { user } = useSession()

  const isAccountProtectFlow =
    isAccountProtectEnabled && searchParams.trigger === VerificationTrigger.ApRegistration
  const isPhoneNumberValid = user?.verification.phone.valid
  const userId = user?.id

  const refUrl = useRefUrl(SECURITY_SETTINGS_2FA_URL)

  useEffect(() => {
    if (isPhoneNumberValid) {
      navigateToPage(SECURITY_SETTINGS_URL)
    }
  }, [isPhoneNumberValid])

  const translate = useTranslate('settings.security.create_phone_number')

  const getValidationError = () => error && <Validation text={error} theme="warning" />

  const handleSendClick = async (number: string) => {
    if (!userId) return

    setIsButtonDisabled(true)
    setIsTooManyAttempts(false)
    setPhoneNumber(number)

    const response = await createPhoneNumber(userId, number)

    if (response.status === HttpStatus.TooManyRequests) {
      setIsTooManyAttempts(true)

      return
    }

    if ('errors' in response) {
      const validationError = response.errors[0]

      if (validationError) setError(validationError.value)
    } else {
      setPhoneNumberData(response.phone_number)
    }

    setIsButtonDisabled(false)
  }

  const resubmitForm = () => {
    handleSendClick(phoneNumber)
  }

  useDataDomeCaptcha(() => resubmitForm())

  const renderCreatePhoneNumberForm = () => (
    <PhoneNumberForm
      isAccountProtectFlow={isAccountProtectFlow}
      onConfirm={handleSendClick}
      isButtonDisabled={isButtonDisabled}
      validation={getValidationError()}
    />
  )

  const renderPhoneVerification = () => {
    if (!phoneNumberData) return null

    return (
      <TwoFactorVerification
        maskedNumber={phoneNumberData.number}
        twoFAId={phoneNumberData.two_fa_id}
        verificationType={VerificationType.Phone}
        nextResendAvailableIn={phoneNumberData.next_resend_available_in}
        showResend={phoneNumberData.show_resend_option}
        refUrl={isAccountProtectFlow ? ROOT_URL : refUrl}
        faqEntryType={
          isAccountProtectFlow ? FaqEntryType.AccontProtectPhoneVerificationNotWorking : undefined
        }
      />
    )
  }

  return (
    <>
      <div className="narrow-container">
        {phoneNumberData ? renderPhoneVerification() : renderCreatePhoneNumberForm()}
      </div>
      {isTooManyAttempts && <Notification body={translate('error')} />}
    </>
  )
}

export default CreatePhoneNumber
